<template>
    <component
        :is="method === 'offline'
            ? 'charges-store'
            : 'payment-intents-store'
        "
        v-slot="{
            sid,
            store,
            error,
            is_loading: is_saving
        }"
        @success="$emit('success', arguments[0])"
        @error="$emit('error', arguments[0])"
    >
        <div class="mb-6 bg-white rounded shadow">
            <header class="flex items-center px-4 border-b h-18 sm:px-6">
                <h1 class="flex-grow text-lg font-normal">Make a payment</h1>

                <div class="flex-none w-6 h-6 mr-3">
                    <base-svg
                        name="icon-lock"
                        class="w-full h-full"
                    />
                </div>
            </header>

            <base-form @submit="store(form)">
                <div class="px-4 pt-6 border-b sm:px-6">
                    <validator-alert
                        v-if="$error.is(error, { exclude: [400] })"
                        :sid="sid"
                    />

                    <form-section>
                        <template slot="title">Amount</template>
                        <template slot="description">
                            How much do you want to pay?
                        </template>

                        <grid-layout>
                            <grid-layout-item class="md:w-1/2">
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="amount"
                                >
                                    <template slot="label">Amount</template>

                                    <div class="relative">
                                        <input-text
                                            v-model="form.amount"
                                            :sid="sid"
                                            :disabled="is_saving"
                                            type="tel"
                                            name="amount"
                                            class="pl-7"
                                        />
                                        <div class="absolute inset-y-0 left-0 flex items-center px-3 pt-1 margin-px text-grey-darker">
                                            &pound;
                                        </div>
                                    </div>
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 pt-6 border-b sm:px-6">
                    <form-section>
                        <template slot="title">Method</template>
                        <template slot="description">
                            How would you like to pay?
                        </template>

                        <grid-layout>
                            <grid-layout-item>
                                <form-item
                                    v-if="!$gate.role('landlord')"
                                    :sid="sid"
                                    :is-required="true"
                                    name="method"
                                >
                                    <template slot="label">Method</template>

                                    <grid-layout>
                                        <grid-layout-item class="mb-3 md:mb-0 sm:w-1/2">
                                            <input-radio-card
                                                v-model="method"
                                                value="bank_card"
                                                name="method"
                                                :disabled="is_saving"
                                                class="w-full"
                                            >
                                                <template slot="label">Card</template>
                                                <template slot="hint">
                                                    Pay online with your bank card. It's quick and safe.
                                                </template>
                                            </input-radio-card>
                                        </grid-layout-item>

                                        <grid-layout-item class="sm:w-1/2">
                                            <input-radio-card
                                                v-model="method"
                                                value="offline"
                                                name="method"
                                                :disabled="is_saving"
                                                class="w-full"
                                            >
                                                <template slot="label">Offline</template>
                                                <template slot="hint">
                                                    Record an offline payment (i.e. cash in hand).
                                                </template>
                                            </input-radio-card>
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item v-if="method === 'offline'">
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="note"
                                >
                                    <template slot="label">Note</template>

                                    <input-text
                                        v-model="form.note"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="note"
                                        placeholder="e.g. Paid cash in hand"
                                    />
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item v-else>
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="bank_card"
                                >
                                    <template slot="label">Bank Card</template>

                                    <input-bank-card
                                        v-model="form.bank_card"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="bank_card"
                                    />

                                    <template slot="hint">
                                        Want to add a
                                        <router-link
                                            :to="{
                                                name: $router.fix('dashboard.*.bank-cards.create')
                                            }"
                                            class="text-blue"
                                        >
                                            new bank card?
                                        </router-link>
                                    </template>
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 pt-6 border-b sm:px-6">
                    <form-section>
                        <template slot="title">Summary</template>
                        <template slot="description">
                            Here's a breakdown of what you're paying for
                        </template>

                        <div class="relative mb-6 rounded shadow pt-1-5">
                            <div class="absolute inset-x-0 top-0 rounded-t bg-blue h-1-5" />

                            <div class="px-4 py-6 -mt-6 text-sm">
                                <div
                                    v-for="(item, index) in items"
                                    :key="index"
                                    v-show="show_more || index < number_of_items_visible"
                                    class="flex mt-6"
                                >
                                    <div class="flex-grow pr-6 truncate">
                                        <truncate-hack>{{ item.title }}</truncate-hack>

                                        <div class="text-xs text-label">
                                            {{ item.object }} - {{ item.price | money(0) }} - {{ item.due_at | moment('DD/MM/YYYY') }}
                                        </div>
                                    </div>

                                    <div class="flex-none font-semibold text-right">
                                        {{ item.amount | money }}

                                        <div class="text-xs text-label">
                                            Still due: {{ item.due | money(0) }}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    v-if="items.length > number_of_items_visible"
                                    type="button"
                                    class="mt-6 text-sm rounded text-blue focus:outline-none focus:shadow-outline"
                                    @click="show_more = !show_more"
                                >
                                    <div class="flex items-center">
                                        <template v-if="show_more">
                                            show less
                                        </template>
                                        <template v-else>
                                            + {{ items.length - number_of_items_visible }} more
                                        </template>

                                        <base-svg
                                            :name="show_more ? 'icon-cheveron-up' : 'icon-cheveron-down'"
                                            :key="show_more ? 'icon-cheveron-up' : 'icon-cheveron-down'"
                                            class="w-5 h-5 fill-primary-blue"
                                        />
                                    </div>
                                </button>

                                <div
                                    v-if="items.length === 0"
                                    class="mt-6 italic text-center text-grey"
                                >
                                    There's nothing here!
                                </div>
                            </div>

                            <div class="flex items-center px-4 py-6 bg-grey-bg">
                                <div class="flex-grow">
                                    Total
                                </div>

                                <div class="font-bold">
                                    {{ total | money }}
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="amount > total"
                            class="flex mb-6"
                        >
                            <base-svg
                                name="icon-important-circle"
                                class="w-5 h-5 mr-3px"
                            />

                            <span class="text-xs leading-tight text-grey-darker mt-2px">
                                You've entered that is higher than there is available to
                                pay. You will only be charged <strong>{{ total | money }}</strong>
                            </span>
                        </div>
                    </form-section>
                </div>

                <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                    <base-button
                        :is-loading="is_saving"
                        :disabled="items.length === 0"
                        class="w-full btn-blue md:w-auto"
                    >
                        Pay {{ total | money(0) }}
                    </base-button>
                </div>

                <slot
                    name="modals"
                    :sid="sid"
                />
            </base-form>
        </div>
    </component>
</template>

<script>
    import _ from 'lodash'
    import moment from 'moment'
    import ChargesStore from './charges-store'
    import InputRadioCard from './input-radio-card'
    import InputBankCard from './input-bank-card'
    import PaymentIntentsStore from './payment-intents-store'

    export default {
        components: {
            ChargesStore,
            InputRadioCard,
            InputBankCard,
            PaymentIntentsStore
        },
        props: {
            initialAmount: {
                type: Number,
                default: () => 0
            },
            rents: {
                type: Array,
                default: () => []
            },
            fees: {
                type: Array,
                default: () => []
            }
        },
        data () {
            return {
                number_of_items_visible: 3,
                show_more: false,
                method: this.$gate.role('landlord') ? 'offline' : 'bank_card',
                form: {
                    amount: (this.initialAmount / 100).toString(),
                    note: '',
                    items: [],
                    bank_card: null
                }
            }
        },
        computed: {
            amount () {
                return _.chain(this.form.amount)
                    .replace(/[,]/g, '')
                    .thru(amount => {
                        return new Intl.NumberFormat('en-GB', {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2
                        }).format(amount)
                    })
                    .replace(/[,.]/g, '')
                    .toInteger()
                    .value()
            },
            total () {
                return _.reduce(this.items, (carry, item) => {
                    return carry + item.amount
                }, 0)
            },
            items () {
                let amount = _.clone(this.amount)

                return _.chain([])
                    .concat(_.map(this.rents, rent => {
                        return _.chain(rent)
                            .pick(['object', 'uuid', 'due_at'])
                            .extend({
                                title: moment(rent.due_at).format('MMMM'),
                                price: _.toInteger(rent.amount),
                                amount: _.toInteger(rent.payable),
                                payable: _.toInteger(rent.payable)
                            })
                            .value()
                    }))
                    .concat(_.map(this.fees, fee => {
                        return _.chain(fee)
                            .pick(['object', 'uuid', 'due_at'])
                            .extend({
                                title: fee.title,
                                price: _.toInteger(fee.amount),
                                amount: _.toInteger(fee.payable),
                                payable: _.toInteger(fee.payable)
                            })
                            .value()
                    }))
                    .sortBy('due_at')
                    .map(item => {
                        if (amount <= 0) return false

                        const price = amount > item.amount ? item.amount : amount

                        if (price <= 0) return false

                        amount -= price

                        return _.extend(item, {
                            amount: price,
                            due: item.payable - price
                        })
                    })
                    .filter()
                    .value()
            }
        },
        watch: {
            items () {
                this.setFormItems()
            }
        },
        created () {
            this.setFormItems()
        },
        methods: {
            setFormItems () {
                this.form.items = _.map(this.items, item => {
                    return _.pick(item, ['object', 'uuid', 'amount'])
                })
            }
        }
    }
</script>
